import { useMemo, useState } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors, metricGradients } from "../constants/designTokens";
import { sortByMonthTimeline } from "../utils/monthTimeline";

function ChartTooltip({ active, payload, label, percentMode = false }) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ p: 1.2, bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 900, color: colors.text }}>
        {label}
      </Typography>
      {payload.map((item) => (
        <Typography key={item.dataKey} sx={{ fontSize: 12, color: item.color || item.fill }}>
          {item.name}: {percentMode ? `${Number(item.value || 0).toFixed(1)}%` : `R$ ${Number(item.value || 0).toFixed(1)}M`}
        </Typography>
      ))}
    </Paper>
  );
}

export default function ChartCard({
  title,
  subtitle,
  data = [],
  series = [],
  height = 300,
  headerGradient = "purple",
  viewTabs = [],
  defaultView = "",
}) {
  const [mode, setMode] = useState("barras");
  const [activeView, setActiveView] = useState(defaultView || viewTabs[0]?.key || "");

  const currentConfig =
    viewTabs.find((item) => item.key === activeView) ||
    (viewTabs.length ? viewTabs[0] : { series, percentMode: false });

  const resolvedSeries = currentConfig.series || series;
  const percentMode = Boolean(currentConfig.percentMode);
  const orderedData = useMemo(() => sortByMonthTimeline(data), [data]);

  const gradient = metricGradients[headerGradient] || metricGradients.purple;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        overflow: "hidden",
        boxShadow: "0 16px 35px rgba(24, 39, 75, 0.07)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ px: 2.4, py: 2.1, borderBottom: `1px solid ${colors.border}` }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: "#fff",
              background: gradient,
            }}
          >
            <BarChartRounded sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ color: colors.text, fontWeight: 900 }}>{title}</Typography>
            {subtitle ? (
              <Typography sx={{ color: colors.softMuted, fontSize: 12 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          {viewTabs.length ? (
            <Stack direction="row" spacing={0.6}>
              {viewTabs.map((item) => {
                const selected = item.key === activeView;
                return (
                  <Button
                    key={item.key}
                    onClick={() => setActiveView(item.key)}
                    sx={{
                      minWidth: "auto",
                      px: 1.4,
                      py: 0.7,
                      borderRadius: 1.7,
                      textTransform: "none",
                      fontSize: 12,
                      fontWeight: 700,
                      color: selected ? "#fff" : colors.softMuted,
                      bgcolor: selected ? "#121d34" : "transparent",
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          ) : null}

          <Stack direction="row" spacing={0.4} sx={{ bgcolor: colors.surfaceSoft, p: 0.5, borderRadius: 2 }}>
            <Button
              size="small"
              startIcon={<BarChartRounded />}
              onClick={() => setMode("barras")}
              sx={{
                bgcolor: mode === "barras" ? colors.surface : "transparent",
                color: mode === "barras" ? colors.text : colors.muted,
                borderRadius: 1.5,
                px: 1.5,
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              Barras
            </Button>
            <Button
              size="small"
              startIcon={<TrendingUpRounded />}
              onClick={() => setMode("area")}
              sx={{
                bgcolor: mode === "area" ? colors.surface : "transparent",
                color: mode === "area" ? colors.text : colors.muted,
                borderRadius: 1.5,
                px: 1.5,
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              Area
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{ height, px: { xs: 1, md: 2 }, pt: 2, pb: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          {mode === "area" ? (
            <AreaChart data={orderedData}>
              <defs>
                {resolvedSeries.map((item) => (
                  <linearGradient key={item.key} id={`gradient-${item.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={item.color} stopOpacity={0.24} />
                    <stop offset="95%" stopColor={item.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid stroke="var(--ac-border, #e8eef7)" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="mes_ano" tick={{ fill: "var(--ac-soft-muted, #7c8ca8)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis
                tickFormatter={(value) => (percentMode ? `${Number(value || 0).toFixed(0)}%` : `${Number(value || 0).toFixed(1)}M`)}
                tick={{ fill: "var(--ac-soft-muted, #7c8ca8)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip percentMode={percentMode} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {resolvedSeries.map((item) => (
                <Area
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.name}
                  stroke={item.color}
                  fill={`url(#gradient-${item.key})`}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </AreaChart>
          ) : (
            <BarChart data={orderedData} barGap={6}>
              <CartesianGrid stroke="var(--ac-border, #e8eef7)" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="mes_ano" tick={{ fill: "var(--ac-soft-muted, #7c8ca8)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis
                tickFormatter={(value) => (percentMode ? `${Number(value || 0).toFixed(0)}%` : `${Number(value || 0).toFixed(1)}M`)}
                tick={{ fill: "var(--ac-soft-muted, #7c8ca8)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip percentMode={percentMode} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {resolvedSeries.map((item) => (
                <Bar
                  key={item.key}
                  dataKey={item.key}
                  name={item.name}
                  fill={item.color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={16}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
